'use client'

import CreationToolError from 'creation/src/creation-tools/types/error'
import CreationToolOutputType from 'creation/src/creation-tools/types/output-type'
import { validateSourceByOutput } from 'creation/src/creation-tools/utils/validate-source'
import { useEffect, useState, type ReactNode } from 'react'
import Loader from '@/app/components/loader'
import { wrapperBlock } from '../presentation/blocks'
import { useCreationStore } from '../store/provider'
import WelcomeErrorModal from './error-modal'
import { WelcomeContext } from './welcome-context'

type Props = {
    children: ReactNode
}

/**
 * Legacy structural reference: components/welcome/index.tsx:60-135 — the
 * `CreationToolsWelcomeContext.Provider` plus the `error ? <Error/> :
 * <Container/>` swap.
 *
 * Client-boundary decision: `gifmaker/page.tsx` and `upload/page.tsx` are
 * server components (they call `getUser()`), so neither can hold the error
 * state itself. This component is the client boundary instead — both pages
 * wrap their existing `<WelcomeContainer>` tree in it
 * (`<WelcomeShell><WelcomeContainer>...</WelcomeContainer></WelcomeShell>`),
 * which puts `WelcomeContext` above `file-button.tsx` and `url-input.tsx`
 * (both descendants of `WelcomeContainer`) so their catch blocks can report
 * into it, and gives this component the authority to swap their entire
 * subtree for the error modal.
 *
 * `WelcomeContainer` itself (container.tsx) is deliberately left untouched
 * — it's the success-state box Task 13 measured and Task 18 diffs. The
 * error branch below gets its own bare flex wrapper matching legacy's
 * `WrapperBlock` (`align-items: center; display: flex; flex-direction:
 * column; justify-content: center; min-height: calc(100vh - 70px); width:
 * 100%`) — the same class `container.tsx` uses on its own outer div,
 * duplicated here rather than factored out of that file for the reason
 * above. The loading branch reuses the same wrapper, matching legacy's own
 * `<WrapperBlock>{isLoading ? <Loader size="large" /> : <Container>...`
 * (components/welcome/index.tsx:110-136) — `isLoading` swaps out the whole
 * success-state subtree, same as `error` does, checked second so an error
 * that lands while a request is in flight still wins (see file-button.tsx's
 * catch, which sets both `error` and leaves `isLoading` true — legacy has
 * the same ordering-dependent quirk).
 *
 * `userRestrictionAccess` is read off the creation store and threaded into
 * `WelcomeErrorModal` — see error-modal.tsx for the permission gate this
 * feeds.
 */
const WelcomeShell = ({ children }: Props) => {
    const setFiles = useCreationStore((state) => state.setFiles)
    const setIsDraggingFile = useCreationStore((state) => state.setIsDraggingFile)
    const userRestrictionAccess = useCreationStore((state) => state.userRestrictionAccess)
    const [error, setError] = useState<CreationToolError>()
    const [errorSource, setErrorSource] = useState<File | string>()
    const [isLoading, setIsLoading] = useState(false)

    // Legacy: `useEffect(() => setIsDraggingFile(false), [error])` — clears
    // any lingering drag-hover state on the button grid the moment an error
    // lands, so going back doesn't show a stale "Drop File" label.
    useEffect(() => {
        setIsDraggingFile(false)
    }, [error, setIsDraggingFile])

    const onGoBack = () => {
        setError(undefined)
        setErrorSource(undefined)
        setIsLoading(false)
    }

    const onChangeOutputType = async (outputType: CreationToolOutputType) => {
        if (!errorSource) return

        try {
            const validatedFile = await validateSourceByOutput(errorSource, outputType)
            setFiles([validatedFile], outputType)
        } catch (err) {
            setError(err as CreationToolError)
        }
    }

    return (
        <WelcomeContext.Provider value={{ error, errorSource, isLoading, setError, setErrorSource, setIsLoading }}>
            {error ? (
                <div className={wrapperBlock}>
                    <WelcomeErrorModal
                        className="w-[400px] shadow-[0_6px_9px_rgba(0,0,0,0.5)]"
                        error={error}
                        userRestrictionAccess={userRestrictionAccess}
                        onGoBack={onGoBack}
                        onChangeOutputType={onChangeOutputType}
                    />
                </div>
            ) : isLoading ? (
                <div className={wrapperBlock}>
                    <Loader />
                </div>
            ) : (
                children
            )}
        </WelcomeContext.Provider>
    )
}

export default WelcomeShell
